import { useCallback, useEffect, useState } from 'react';
import {
  analyticsAddNewLocation,
  analyticsAddRiderLocation,
  analyticsDeleteRiderLocation,
  analyticsEditedAssignedRiderLocation,
  analyticsEditRiderLocation,
} from '@rideiq/api/services/Analytics';
import {
  associateLocation as callAssociateLocation,
  dissociateLocation,
  getAssociatedLocations,
} from '@rideiq/api/services/Riders';
import { useToastStore } from '@rideiq/shared/stores/toastStore';
import filterObjectByKeys from '@rideiq/utils/helpers/filterObjectByKeys';
import _ from 'lodash';

import { RiderLocation } from './Location/Location.types';

interface IUseLocationsForRider {
  isLoading: boolean;
  associatedLocations: RiderLocation[];
  addLocation: (data: RiderLocationFormData, locationDetails: RiderLocation) => Promise<boolean>;
  editLocation: (data: RiderLocationFormData, locationDetails: RiderLocation) => Promise<boolean>;
  removeLocation: (locationId: number) => Promise<boolean>;
}

export const useLocationsForRider = (riderSlug: string): IUseLocationsForRider => {
  const [associatedLocations, setAssociatedLocations] = useState<RiderLocation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { addToast } = useToastStore();

  const fetchLocations = useCallback(async () => {
    setIsLoading(true);
    const response = await getAssociatedLocations(riderSlug);

    if (response?.type === 'success' && response.data.locations) {
      setAssociatedLocations(response.data.locations);
    }

    setIsLoading(false);
  }, []);

  const addLocation = (data: RiderLocationFormData, locationDetails: RiderLocation) => {
    if (data.mustBeMet && typeof data.locationProtocol === 'string') {
      analyticsAddNewLocation(data.locationProtocol.replace(/_/g, ' '), locationDetails.locationType);
    }

    return associateLocation(data, locationDetails);
  };

  const afterChangeToLocation = (riderLocationData: RiderLocationFormData, locationDetails: RiderLocation) => {
    const existingLocation = associatedLocations.find((loc) => loc.id === locationDetails.id);

    const locationUpdates = {
      ...filterObjectByKeys(locationDetails, locationAttributes),
      ...riderLocationData,
    };

    const associatedLocationsWithNewLocation = [
      ...(!existingLocation ? [locationUpdates] : []),
      ...associatedLocations,
    ];

    if (riderLocationData.primary || existingLocation) {
      const locationsWithUpdates = associatedLocationsWithNewLocation.map((loc) => {
        if (
          loc.locationType === locationDetails.locationType &&
          loc.primary === true &&
          loc.id !== locationUpdates.id
        ) {
          return { ...loc, primary: false };
        }
        if (existingLocation?.id === loc.id) {
          return { ...existingLocation, ...locationUpdates };
        }

        return loc;
      });

      setAssociatedLocations(locationsWithUpdates);
    } else {
      setAssociatedLocations(associatedLocationsWithNewLocation);
    }
  };

  const associateLocation = async (data: RiderLocationFormData, locationDetails: RiderLocation) => {
    const { locationId, replaceLocation, ...formData } = data;

    if (replaceLocation?.id) {
      const locationAlreadyAssociated = associatedLocations.find((loc) => loc.id === replaceLocation.id);

      // if location that will be replacing is NOT associated, must associate to rider before replacing
      if (!locationAlreadyAssociated) {
        const response = await callAssociateLocation(riderSlug, replaceLocation.id, {
          primary: false,
          mustBeMet: formData.mustBeMet,
          locationProtocol: formData.locationProtocol,
          authorizedContactGroups: formData.authorizedContactGroups,
        });

        if (response?.type === 'error') {
          response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));

          return false;
        }
      }
    }

    const response = await callAssociateLocation(riderSlug, locationId, {
      primary: formData.primary,
      mustBeMet: formData.mustBeMet,
      locationProtocol: formData.locationProtocol,
      authorizedContactGroups: formData.authorizedContactGroups,
      ...(replaceLocation?.id && { replaceLocation }),
    });

    if (response?.type === 'error') {
      response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));

      return false;
    }
    addToast({ severity: 'success', message: response.data.message });

    analyticsAddRiderLocation(!!formData.primary);

    if (replaceLocation?.id) {
      await fetchLocations();
    } else {
      afterChangeToLocation(data, locationDetails);
    }

    return true;
  };

  const editLocation = async (data: RiderLocationFormData, locationDetails: RiderLocation) => {
    const previousState = locationDetails;

    analyticsEditRiderLocation(!!data.primary);

    if (data.mustBeMet && typeof data.locationProtocol === 'string') {
      analyticsEditedAssignedRiderLocation(data.locationProtocol.replace(/_/g, ' '), locationDetails.locationType);
    }

    if (
      data.replaceLocation?.id ||
      previousState?.primary !== data.primary ||
      previousState?.mustBeMet !== data.mustBeMet ||
      previousState?.locationProtocol !== data.locationProtocol ||
      !_.isEqual(previousState?.authorizedContactGroups, data.authorizedContactGroups)
    ) {
      return associateLocation(
        {
          ...data,
          ...(previousState?.locationProtocol !== data.locationProtocol &&
            !data.authorizedContactGroups && {
              authorizedContactGroups: previousState?.authorizedContactGroups,
            }),
        },
        locationDetails
      );
    }
    afterChangeToLocation(data, locationDetails);

    return true;
  };

  const removeLocation = async (locationId) => {
    const response = await dissociateLocation(riderSlug, locationId);

    if (response?.type === 'error') {
      response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));

      return false;
    }
    analyticsDeleteRiderLocation();
    setAssociatedLocations(associatedLocations.filter((location) => location.id !== locationId));

    return true;
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  return { isLoading, associatedLocations, addLocation, editLocation, removeLocation };
};

const locationAttributes = [
  'id',
  'dropoffProcedureInstructions',
  'locationType',
  'name',
  'address',
  'placeId',
  'pickupProcedureInstructions',
  'primary',
  'replaceLocation',
  'streetAddressTwo',
];
